import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ChevronDown, MessageCircle, Clock } from "lucide-react";
import { ctaClick } from "@/lib/analytics";
import { useIsMobile } from "@/hooks/useIsMobile";

const blocks = [
  {
    id: "recurring",
    headline: "Cobros recurrentes que se ejecutan solos",
    subcopy: "Define la frecuencia y Kubo cobra puntual. Tú solo monitoreas.",
    chips: ["Frecuencias flexibles", "Próximos cobros", "Estados"],
    screenshot: "https://i.imgur.com/7VomZUr.png",
    mobileFirst: true,
  },
  {
    id: "dashboard",
    headline: "Visibilidad total de tu flujo de caja",
    subcopy: "Ingresos del mes, próximos cobros y alertas operativas.",
    chips: ["Ingresos del mes", "Próximos cobros", "Alertas"],
    screenshot: "https://i.imgur.com/GcnM96A.png",
    mobileFirst: true,
  },
  {
    id: "transactions",
    headline: "Todo en un solo historial",
    subcopy: "Filtra, concilia y encuentra cualquier pago en segundos.",
    chips: ["Filtros por fecha", "Conciliación rápida", "Tasa de éxito"],
    screenshot: "https://i.imgur.com/tskL2KR.png",
    mobileFirst: true,
    showTimeCta: true,
  },
  {
    id: "single-payments",
    headline: "Pagos únicos sin fricción",
    subcopy: "Crea órdenes de cobro y da seguimiento al estado.",
    chips: ["Órdenes de cobro", "Estado del pago", "Búsqueda rápida"],
    screenshot: "https://i.imgur.com/trl68MA.png",
    mobileFirst: false,
  },
  {
    id: "brand",
    headline: "Tu marca, tu checkout",
    subcopy: "Tu logo y datos visibles para aumentar confianza al pagar.",
    chips: ["Logo y colores", "Contacto del comercio", "Enlaces claros"],
    screenshot: "https://i.imgur.com/B1zs4DY.png",
    mobileFirst: false,
    showTimeCta: true,
  },
];

function BlockCard({ block, index, isDesktop, onDemoClick }) {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [20, -20]);

  const isReversed = isDesktop && index % 2 !== 0;
  const isEager = index === 0;
  const shouldAnimate = !prefersReducedMotion;

  return (
    <motion.div
      ref={ref}
      initial={shouldAnimate ? { opacity: 0, y: isMobile ? 24 : 40 } : false}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: isMobile ? 0.35 : 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="grid md:grid-cols-2 gap-6 md:gap-12 items-center"
    >
      {/* Copy side */}
      <div className={isReversed ? "md:order-2" : ""}>
        <motion.h3
          initial={shouldAnimate ? { opacity: 0, y: 12 } : false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.3 : 0.45, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="font-['Cabinet_Grotesk'] font-black tracking-tighter text-[#075a22] text-2xl md:text-4xl leading-tight"
        >
          {block.headline}
        </motion.h3>

        <motion.p
          initial={shouldAnimate ? { opacity: 0, y: 10 } : false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.3 : 0.45, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-3 md:mt-4 text-base md:text-lg text-[#111111]/80 leading-relaxed max-w-lg"
        >
          {block.subcopy}
        </motion.p>

        <div className="mt-4 md:mt-5 flex flex-wrap gap-2">
          {block.chips.map((chip, i) => (
            <motion.span
              key={chip}
              initial={shouldAnimate ? { opacity: 0, y: 6 } : false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: isMobile ? 0.25 : 0.35, delay: 0.12 + i * 0.04, ease: [0.22, 1, 0.36, 1] }}
              className="text-xs md:text-sm font-semibold bg-[#111111]/5 text-[#111111]/75 px-3 py-1.5 rounded-lg border border-[#111111]/8"
            >
              {chip}
            </motion.span>
          ))}
        </div>

        <motion.div
          initial={shouldAnimate ? { opacity: 0 } : false}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.25 : 0.4, delay: 0.2 }}
          className="mt-5 md:mt-6 flex flex-col items-start gap-2"
        >
          <button
            onClick={() => onDemoClick()}
            className="inline-flex items-center gap-1.5 text-[#a68a6d] hover:text-[#8f7659] font-bold text-sm transition-colors"
          >
            Verlo en vivo
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
          {block.showTimeCta && (
            <button
              onClick={() => onDemoClick()}
              className="inline-flex items-center gap-1.5 text-[#111111]/50 hover:text-[#111111]/70 text-xs font-medium transition-colors"
            >
              <Clock className="w-3 h-3" strokeWidth={2} />
              Cuánto tiempo toma activarlo
            </button>
          )}
        </motion.div>
      </div>

      {/* Screenshot side */}
      <motion.div
        style={{ y: shouldAnimate ? y : 0 }}
        className={`relative ${isReversed ? "md:order-1" : ""}`}
      >
        <div className="relative rounded-xl md:rounded-2xl overflow-hidden border border-[#111111]/10 shadow-[0_16px_40px_-15px_rgba(17,17,17,0.15)] md:shadow-[0_20px_60px_-20px_rgba(17,17,17,0.2)] bg-white">
          <img
            src={block.screenshot}
            alt={block.headline}
            loading={isEager ? "eager" : "lazy"}
            width={800}
            height={500}
            className="w-full h-auto object-cover"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Features({ onDemoClick }) {
  const [showMore, setShowMore] = useState(false);

  const mobileBlocks = blocks.filter((b) => b.mobileFirst);
  const extraBlocks = blocks.filter((b) => !b.mobileFirst);

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-14 md:py-24 bg-[#F2EDE3]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-8 md:mb-14"
        >
          <span className="text-sm md:text-base font-bold uppercase tracking-[0.2em] text-[#111111]">
            Producto
          </span>
          <h2 className="mt-3 md:mt-4 font-['Cabinet_Grotesk'] font-black tracking-tighter leading-[0.95] text-[#075a22] text-3xl sm:text-4xl md:text-6xl">
            Automatiza tus cobros.
            <br />
            <span className="text-[#a68a6d]">Sin llamadas. Sin Excel.</span>
          </h2>
        </motion.div>

        {/* Desktop: all 5 blocks */}
        <div className="hidden md:flex flex-col gap-16 md:gap-20">
          {blocks.map((block, i) => (
            <BlockCard
              key={block.id}
              block={block}
              index={i}
              isDesktop
              onDemoClick={onDemoClick}
            />
          ))}

          {/* Desktop CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            className="flex flex-col items-center text-center"
          >
            <button
              onClick={() => onDemoClick()}
              className="group inline-flex items-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white text-lg font-bold px-8 py-5 rounded-full transition-all hover:scale-[1.03] shadow-[0_20px_50px_rgba(166,138,109,0.35)]"
            >
              Quiero automatizar mis cobros
              <ArrowUpRight
                className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.5}
              />
            </button>
            <p className="mt-3 text-sm text-[#111111]/50">
              Sin llamadas. Sin Excel.
            </p>
          </motion.div>
        </div>

        {/* Mobile: 3 visible + accordion */}
        <div className="md:hidden flex flex-col gap-10">
          {mobileBlocks.map((block, i) => (
            <div key={block.id}>
              <BlockCard
                block={block}
                index={i}
                isDesktop={false}
                onDemoClick={onDemoClick}
              />
              {/* Primary CTA after block 2 (Dashboard) */}
              {i === 1 && (
                <div className="mt-8 flex flex-col items-center text-center">
                  <button
                    onClick={() => {
                      ctaClick("features_mobile_primary", "features");
                      onDemoClick();
                    }}
                    className="group inline-flex items-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white text-base font-bold px-6 py-4 rounded-full transition-all hover:scale-[1.03] shadow-[0_12px_30px_rgba(166,138,109,0.3)]"
                  >
                    <MessageCircle className="w-5 h-5" strokeWidth={2} />
                    Agenda una demo por WhatsApp
                  </button>
                  <p className="mt-2 text-xs text-[#111111]/50">
                    Te mostramos tu caso en 15 min.
                  </p>
                </div>
              )}
            </div>
          ))}

          {/* Ver más accordion */}
          <div>
            <button
              onClick={() => setShowMore(!showMore)}
              className="w-full flex items-center justify-center gap-2 py-3 text-[#a68a6d] font-bold text-base transition-colors hover:text-[#8f7659]"
            >
              {showMore ? "Ver menos" : "Ver más"}
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-300 ${
                  showMore ? "rotate-180" : ""
                }`}
                strokeWidth={2.5}
              />
            </button>

            {showMore && (
              <div className="flex flex-col gap-10 overflow-hidden">
                {extraBlocks.map((block, i) => (
                  <BlockCard
                    key={block.id}
                    block={block}
                    index={mobileBlocks.length + i}
                    isDesktop={false}
                    onDemoClick={onDemoClick}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Mobile closing CTA */}
          <div className="flex flex-col items-center text-center pt-2">
            <button
              onClick={() => {
                ctaClick("features_mobile_close", "features");
                onDemoClick();
              }}
              className="group inline-flex items-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white text-base font-bold px-6 py-4 rounded-full transition-all hover:scale-[1.03] shadow-[0_12px_30px_rgba(166,138,109,0.3)]"
            >
              Quiero automatizar mis cobros
              <ArrowUpRight
                className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.5}
              />
            </button>
            <p className="mt-2 text-xs text-[#111111]/50">
              Sin llamadas. Sin Excel.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
